import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';

export default function DemographicsPage() {
  const keyStats = [
    { label: 'Total Population', value: '680,000+', icon: 'ri-group-line', color: 'bg-[#E8C4C4]' },
    { label: 'Median Household Income', value: '$52,000', icon: 'ri-money-dollar-circle-line', color: 'bg-[#C8D5B9]' },
    { label: 'Hispanic Population', value: '82%', icon: 'ri-pie-chart-line', color: 'bg-[#D5D5D5]' },
    { label: 'Small Businesses', value: '15,000+', icon: 'ri-store-2-line', color: 'bg-[#D9C8B4]' },
    { label: 'Median Age', value: '33 years', icon: 'ri-user-line', color: 'bg-[#E8F5E9]' },
    { label: 'College Educated', value: '28%', icon: 'ri-graduation-cap-line', color: 'bg-[#FFE8D6]' },
  ];

  const industries = [
    { name: 'Healthcare & Social Services', percentage: 18, jobs: '45,000+' },
    { name: 'Retail Trade', percentage: 15, jobs: '38,000+' },
    { name: 'Education Services', percentage: 12, jobs: '30,000+' },
    { name: 'Manufacturing', percentage: 11, jobs: '28,000+' },
    { name: 'Professional Services', percentage: 10, jobs: '25,000+' },
    { name: 'Hospitality & Food Services', percentage: 9, jobs: '23,000+' },
    { name: 'Transportation & Logistics', percentage: 8, jobs: '20,000+' },
    { name: 'Construction', percentage: 7, jobs: '18,000+' },
  ];

  const marketInsights = [
    {
      title: 'Bilingual Market',
      description: 'El Paso is a predominantly bilingual city with 70% of residents speaking both English and Spanish, creating unique opportunities for businesses serving diverse communities.',
      icon: 'ri-translate-2',
      link: '/demographics/bilingual-market',
    },
    {
      title: 'Border Economy',
      description: 'Strategic location on the US-Mexico border facilitates international trade and commerce, with over $80 billion in annual cross-border trade.',
      icon: 'ri-earth-line',
      link: '/demographics/border-economy',
    },
    {
      title: 'Growing Tech Sector',
      description: 'Emerging technology and innovation ecosystem supported by UTEP research and growing startup community.',
      icon: 'ri-computer-line',
      link: '/demographics/growing-tech-sector',
    },
    {
      title: 'Military Presence',
      description: 'Fort Bliss contributes significantly to the local economy with over 40,000 military personnel and civilian employees.',
      icon: 'ri-shield-star-line',
      link: '/demographics/military-presence',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="El Paso Demographics & Market Data - Population, Income & Industry Statistics"
        description="Comprehensive El Paso demographic data for business planning. Access population statistics, household income, industry breakdowns, bilingual market insights, and economic trends for Texas entrepreneurs."
        canonical="/demographics"
        keywords="El Paso demographics, El Paso population, market research El Paso, El Paso economy, business statistics Texas, El Paso market data, consumer demographics"
      />
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <header className="text-center mb-16">
            <h1 className="text-6xl font-bold text-gray-900 mb-6">El Paso Demographics</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Understand your market with comprehensive demographic data and economic insights about El Paso, Texas.
            </p>
          </header>

          <section className="mb-20">
            <h2 className="text-4xl font-bold text-gray-900 mb-10">Key Statistics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {keyStats.map((stat, index) => (
                <article
                  key={index}
                  className="bg-[#FAFAF8] rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className={`w-16 h-16 ${stat.color} rounded-xl flex items-center justify-center mb-6`}>
                    <i className={`${stat.icon} text-3xl text-gray-700`} aria-hidden="true"></i>
                  </div>
                  <div className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
                  <div className="text-sm font-medium text-gray-600">{stat.label}</div>
                </article>
              ))}
            </div>
          </section>

          <section className="mb-20">
            <h2 className="text-4xl font-bold text-gray-900 mb-10">Top Industries</h2>
            <div className="bg-[#FAFAF8] rounded-2xl p-8">
              <div className="space-y-6">
                {industries.map((industry, index) => (
                  <article key={index}>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{industry.name}</h3>
                      <div className="flex items-center space-x-4">
                        <span className="text-sm text-gray-600">{industry.jobs}</span>
                        <span className="text-lg font-bold text-[#D2691E]">{industry.percentage}%</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-[#D2691E] to-[#B8571A] h-full rounded-full transition-all duration-500"
                        style={{ width: `${industry.percentage * 5}%` }}
                        role="progressbar"
                        aria-valuenow={industry.percentage}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={`${industry.name} represents ${industry.percentage}% of El Paso employment`}
                      ></div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="mb-20">
            <h2 className="text-4xl font-bold text-gray-900 mb-10">Market Insights</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {marketInsights.map((insight, index) => (
                <Link
                  key={index}
                  to={insight.link}
                  className="bg-gradient-to-br from-[#1B3A3A] to-[#2C5F4F] rounded-2xl p-8 text-white hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                >
                  <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-6">
                    <i className={`${insight.icon} text-3xl text-white`} aria-hidden="true"></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-4">{insight.title}</h3>
                  <p className="text-sm text-white/90 leading-relaxed mb-4">{insight.description}</p>
                  <div className="flex items-center space-x-2 text-white font-semibold">
                    <span>Learn More</span>
                    <i className="ri-arrow-right-line text-xl" aria-hidden="true"></i>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          <aside>
            <div className="bg-[#FFE8D6] rounded-2xl p-12 text-center">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Need More Detailed Data?</h2>
              <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
                Access comprehensive market research reports, consumer behavior studies, and industry-specific data to inform your business decisions.
              </p>
              <button className="inline-flex items-center space-x-3 bg-[#D2691E] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#B8571A] transition-all shadow-lg cursor-pointer whitespace-nowrap">
                <span>Request Custom Report</span>
                <i className="ri-arrow-right-line text-xl" aria-hidden="true"></i>
              </button>
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}